import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, setDoc, collection, getDocs } from 'firebase/firestore';
import { PAGE_STRUCTURE } from '../pages/AdminPanel';

const AuthContext = createContext();
export function useAuth() { return useContext(AuthContext); }

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");

  // Build default admin profile (all access)
  const makeAdminProfile = (email) => ({
    name: "Admin",
    email: email,
    role: "admin",
    companies: ["salam", "river"],
    pages: PAGE_STRUCTURE.map(p => p.id),
    tabs: PAGE_STRUCTURE.reduce((acc, p) => { if (p.tabs.length) acc[p.id] = p.tabs.map(t => t.id); return acc; }, {}),
    agents: [],
    active: true,
    createdAt: new Date().toISOString(),
  });

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setCurrentUser(user);
        try {
          const snap = await getDoc(doc(db, "users", user.uid));
          if (snap.exists()) {
            const profile = snap.data();
            if (profile.active === false) {
              setAuthError("Account disabled. Contact your admin.");
              await signOut(auth);
              setCurrentUser(null);
              setUserProfile(null);
            } else {
              setUserProfile({ id: user.uid, ...profile });
              setAuthError("");
            }
          } else {
            // Bootstrap: if no users collection yet, auto-create admin
            const usersSnap = await getDocs(collection(db, "users"));
            if (usersSnap.empty) {
              const adminProfile = makeAdminProfile(user.email);
              await setDoc(doc(db, "users", user.uid), adminProfile);
              setUserProfile({ id: user.uid, ...adminProfile });
              setAuthError("");
            } else {
              // User exists in Auth but has no Firestore profile
              setAuthError("No profile found for this account. Contact your admin.");
              await signOut(auth);
              setCurrentUser(null);
              setUserProfile(null);
            }
          }
        } catch (err) {
          console.error("Auth profile load error:", err);
          setAuthError("Error loading profile: " + err.message);
        }
      } else {
        setCurrentUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = useCallback(async (email, password) => {
    setAuthError("");
    try {
      await signInWithEmailAndPassword(auth, email.trim(), password);
    } catch (err) {
      const msg = err.code === "auth/invalid-credential" ? "Invalid email or password"
        : err.code === "auth/user-not-found" ? "User not found"
        : err.code === "auth/wrong-password" ? "Wrong password"
        : err.code === "auth/too-many-requests" ? "Too many attempts. Wait a moment."
        : err.message;
      setAuthError(msg);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    await signOut(auth);
    setCurrentUser(null);
    setUserProfile(null);
  }, []);

  // Refresh profile from Firestore (called after admin edits)
  const refreshProfile = useCallback(async () => {
    if (!currentUser) return;
    const snap = await getDoc(doc(db, "users", currentUser.uid));
    if (snap.exists()) setUserProfile({ id: currentUser.uid, ...snap.data() });
  }, [currentUser]);

  return (
    <AuthContext.Provider value={{ currentUser, userProfile, loading, authError, login, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}
